import React from "react";

import { Button, Select, Stack, Text, TextInput } from "@mantine/core";

import { DatePicker } from "@bjarkehs/react-nice-dates";
import { enAU } from "date-fns/locale";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { getAllPayrates } from "../../data/actions/ratesActions";
import { IState } from "../../data/reducers/state";
import { IPayRate } from "../../types/payrates/PayRate";
import { ITimeSheetEntry } from "../../types/timesheet/Timesheet";

import { DateTime } from "luxon";
import TimesheetApi from "../../data/api/timesheetApi";
import { IAssociation } from "../../types/association/Association";
import { IPerson } from "../../types/persons/person";
import { toDateTime } from "../../util/date+helper";
import AssociationSelector from "../management/Association/AssociationSelector";


interface ICreateTimeSheetEntryProps {
    actions: {
        getAllPayrates: () => void;
    };

    shiftSession?: string
    associationId?: string

    payrates: { [key: string]: IPayRate };

    onCreated: (entry: Partial<ITimeSheetEntry>) => void;
    onCancel: () => void;

    person: IPerson
    associations: IAssociation[]
}

interface ISimplifiedPayrate {
    _id?: string;
    name: string;
    rate: number;
}


export const CreateTimesheetEntry: React.FunctionComponent<ICreateTimeSheetEntryProps> = (props) => {

    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    const [entry, setEntry] = React.useState<{
        day: DateTime
        startTime: DateTime;
        endTime: DateTime;

        payrateId?: string;
        total?: number

        associationId?: string
        shiftSessionId?: string
        description: string
    }>({
        shiftSessionId: props.shiftSession,
        associationId: props.associationId,
        day: DateTime.now(),
        // min of (startOfDay OR now - 30 minutes)
        startTime: (
            Math.abs(DateTime.now().startOf("day").diffNow("minutes").minutes) > 30
                ? DateTime.now().minus({ minutes: 30 })
                : DateTime.now().startOf("day")
        ),
        endTime: DateTime.now(),
        description: ""
    })

    React.useEffect(() => {
        if (Object.keys(props.payrates).length === 0) {
            props.actions.getAllPayrates();
        }

    }, [])

    const selfPayrate = props.person.associations.filter(a => a.associationId == entry.associationId)[0]?.adminPayrate

    let payratesWithAdmin: ISimplifiedPayrate[] = Object.values(props.payrates).filter(pr => !!pr.admin).map(
        pr => ({
            _id: pr._id,
            name: pr.name,
            rate: pr.admin?.rate || 0
        })
    )

    let defaultPayrateId: string | undefined = undefined
    if (selfPayrate) {
        // add self payrate to the list
        defaultPayrateId = 'self'
        payratesWithAdmin.unshift({
            _id: 'self', // special, we'll need to translate this to null
            name: "Personal payrate",
            rate: selfPayrate,
        })
    }

    if (!entry.payrateId) {
        if (payratesWithAdmin.length === 1) {
            defaultPayrateId = payratesWithAdmin[0]._id
        }
        if (defaultPayrateId) {
            setEntry({ ...entry, payrateId: defaultPayrateId })
        }
    }

    let total: number = 0;
    let minutesWorked: number = 0;

    if (entry.payrateId) {
        const rate = (entry.payrateId === 'self'
            ? selfPayrate
            : props.payrates[entry.payrateId]?.admin?.rate
        ) || 0;
        // combine day and start, day and end to get the total hours worked
        const day = { year: entry.day!.year, month: entry.day!.month, day: entry.day!.day }
        const _s = entry.startTime.set(day)
        const _e = entry.endTime.set(day)

        const hoursWorked = _e.diff(_s, "hours").hours

        // round to 2 decimal places
        minutesWorked = _e.diff(_s, "minutes").minutes
        total = rate * hoursWorked
    }

    const submitEnabled = (
        entry.payrateId
        && entry.associationId
        && entry.day
        && entry.startTime
        && entry.endTime
        && minutesWorked > 0
    )

    return <div>
        <Stack gap="md">
            <AssociationSelector
                disabled={!!props.associationId}
                value={entry.associationId}
                useAdminAssociations={false}
                associations={props.associations}
                showLabel={true}
                onChange={(association) => setEntry({ ...entry, associationId: association._id })}
            />
            {payratesWithAdmin.length === 0
                ? <p>No admin pay rates have been configured. Please contact your administrator to set up pay rates.</p>
                : <Stack gap="sm"
                    style={{}}
                >

                    <label className="fw-500" htmlFor="pashifyRate">Pay Rate</label>
                    <Select
                        name="payRate"
                        id="payRate"
                        value={entry.payrateId || ""}
                        disabled={payratesWithAdmin.length === 1}
                        onChange={(value) => setEntry({ ...entry, payrateId: value })}
                        data={[
                            ...(!entry.payrateId ? [{ value: "", label: "Select a pay rate" }] : []),
                            ...payratesWithAdmin.map(pr => ({
                                value: pr._id,
                                label: `$${pr?.rate}: ${pr.name}`
                            }))
                        ]}
                    />
                </Stack>}
            {/* <Stack gap="sm"> */}
            {/*  association */}
            {/* <label className="fw-500" htmlFor="association">Association</label> */}

            {/* </Stack> */}
            <Stack gap="sm">
                {/* day */}
                <label className="fw-500" htmlFor="day">Day</label>
                <DatePicker
                    date={entry.day?.toJSDate()}
                    onDateChange={(date) => setEntry({ ...entry, day: toDateTime(date!) })}
                    locale={enAU}
                >
                    {({ inputProps, focused }) => (
                        <TextInput
                            {...inputProps}
                            type="text"
                            className={`input${focused ? " -focused" : ""}`}
                        />
                    )}
                </DatePicker>
            </Stack>
            <Stack gap="sm">
                {/* start time */}
                <label className="fw-500" htmlFor="startTime">Start Time</label>
                <TextInput
                    type="time"
                    name="startTime"
                    id="startTime"
                    value={entry.startTime?.toFormat("HH:mm")}
                    onChange={(e) => {
                        const newStartTime = DateTime.fromFormat(e.target.value, "HH:mm")
                        if (newStartTime > entry.endTime) {
                            setEntry({ ...entry, startTime: entry.endTime })
                        } else {
                            setEntry({ ...entry, startTime: newStartTime })
                        }
                    }}
                />
            </Stack>
            <Stack gap="sm">
                {/* end time */}
                <label className="fw-500" htmlFor="endTime">End Time</label>
                <TextInput
                    type="time"
                    name="endTime"
                    id="endTime"
                    value={entry.endTime?.toFormat("HH:mm")}
                    onChange={(e) => {
                        const newEndTime = DateTime.fromFormat(e.target.value, "HH:mm")
                        if (newEndTime < entry.startTime) {
                            setEntry({ ...entry, endTime: entry.startTime })
                        } else {
                            setEntry({ ...entry, endTime: newEndTime })
                        }
                    }}
                />
            </Stack>

            <Stack gap="sm">
                <label className="fw-500" htmlFor="description">Description</label>
                <TextInput
                    type="textarea"
                    name="description"
                    id="description"
                    placeholder="Enter a description of the work performed"
                    value={entry.description}
                    onChange={(e) => setEntry({ ...entry, description: e.target.value })}
                />
            </Stack>

            <Text size="sm" fw={500}>
                <b>Duration</b>: {minutesWorked.toFixed(0)} minutes
                {total > 0 && <>
                    <br />
                    <b>Total</b>: ${total.toFixed(2)}
                </>}
            </Text>

            {entry.shiftSessionId && <Text size="sm" fw={500}>
                Note: Creating in the context of a shift session
            </Text>}
            <br />

            <Button
                disabled={!submitEnabled}
                style={{ float: "right" }}
                onClick={() => {
                    const day = { year: entry.day!.year, month: entry.day!.month, day: entry.day!.day }
                    const s = entry.startTime.set(day)
                    const e = entry.endTime.set(day)
                    setIsSaving(true)
                    TimesheetApi.createTimesheetEntry({
                        start: s.toJSDate(),
                        end: e.toJSDate(),
                        payrateId: entry.payrateId === 'self' ? undefined : entry.payrateId,
                        associationId: entry.associationId!,
                        shiftSessionId: entry.shiftSessionId!,
                        description: entry.description
                    }).then(resp => {
                        setIsSaving(false)
                        props.onCreated?.(resp)
                    }).catch(er => {
                        setIsSaving(false)
                        setError(er.message)
                    })
                }}
            >Save</Button>
        </Stack>
    </div >
}

function mapStateToProps(state: IState, ownProps: ICreateTimeSheetEntryProps) {
    const person = state.persons.map[state.persons.self];
    return {
        payrates: state.payrates.map,
        person: person,
        associations: person.associations.map(a => state.associations.map[a.associationId]),
    };
}

function mapDispatchToProps(dispatch) {

    return {
        actions: bindActionCreators({
            getAllPayrates
        }, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateTimesheetEntry);
